import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileText, Loader2, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

function FlagList({ flags }) {
  if (!flags?.length) {
    return <p className="text-xs text-emerald-700" data-testid="manual-ccl-sweep-clean">No issues found by the manual CCL check.</p>;
  }
  return (
    <ul className="space-y-1.5" data-testid="manual-ccl-sweep-flags">
      {flags.map((flag, index) => {
        const blocked = flag.severity === "error";
        return (
          <li
            key={`${flag.code || "flag"}-${index}`}
            className={`flex gap-2 text-xs ${blocked ? "text-red-700" : "text-amber-700"}`}
            data-testid={`manual-ccl-flag-${flag.code || index}`}
          >
            {blocked ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <span>{flag.message || String(flag)}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default function CclManualUpload({ matterRef }) {
  const inputRef = useRef(null);
  const [state, setState] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(Boolean(matterRef));
  const [uploading, setUploading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const loadState = useCallback(async () => {
    if (!matterRef) return;
    try {
      setLoading(true);
      const response = await api.get(`${API}/ccl/manual-upload/${encodeURIComponent(matterRef)}`);
      setState(response.data?.manual_ccl || null);
    } catch (error) {
      if (error?.response?.status !== 404) {
        toast.error(errorMessage(error, "Could not load manual CCL status"));
      }
    } finally {
      setLoading(false);
    }
  }, [matterRef]);

  useEffect(() => { loadState(); }, [loadState]);

  const handleUpload = async () => {
    if (!file || !matterRef) return;
    const form = new FormData();
    form.append("file", file);
    try {
      setUploading(true);
      const response = await api.post(`${API}/ccl/manual-upload/${encodeURIComponent(matterRef)}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState(response.data?.manual_ccl || null);
      setOverride(false);
      setOverrideReason("");
      toast.success(response.data?.sweep?.passed ? "Manual CCL check passed" : "Manual CCL uploaded. Review the check results.");
    } catch (error) {
      toast.error(errorMessage(error, "Manual CCL upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const handleApprove = async () => {
    if (!matterRef) return;
    try {
      setApproving(true);
      const response = await api.post(`${API}/ccl/manual-upload/${encodeURIComponent(matterRef)}/approve`, {
        override,
        override_reason: override ? overrideReason.trim() : "",
      });
      setState(response.data?.manual_ccl || null);
      toast.success("Manual CCL approved. Nothing has been sent.");
    } catch (error) {
      toast.error(errorMessage(error, "Manual CCL approval failed"));
    } finally {
      setApproving(false);
    }
  };

  const handleUndo = async () => {
    if (!matterRef) return;
    try {
      const response = await api.delete(`${API}/ccl/manual-upload/${encodeURIComponent(matterRef)}/approve`);
      setState(response.data?.manual_ccl || null);
      toast.success("Manual CCL approval removed");
    } catch (error) {
      toast.error(errorMessage(error, "Could not undo manual CCL approval"));
    }
  };

  const flags = state?.manual_ccl_sweep_flags || [];
  const passed = Boolean(state?.manual_ccl_sweep_passed);
  const approved = Boolean(state?.manual_ccl_approved_at);
  const canApprove = passed || (override && overrideReason.trim().length > 0);

  return (
    <div className="border-b border-slate-200 bg-white px-3 py-2" data-testid="manual-ccl-upload-panel">
      {approved && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="manual-ccl-approved-banner">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span className="font-medium">Manual CCL approved. Nothing has been sent. It goes to the client only when someone presses Send to Client for Signing.</span>
          <button type="button" onClick={handleUndo} className="ml-auto underline hover:text-emerald-950" data-testid="manual-ccl-undo-btn">Undo</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept=".docx,.pdf,.html"
          className="hidden"
          onChange={(event) => setFile(event.target.files?.[0] || null)}
          data-testid="manual-ccl-file-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          data-testid="manual-ccl-pick-btn"
        >
          <Upload className="h-3.5 w-3.5" /> Upload completed CCL
        </button>
        {file && <span className="max-w-[240px] truncate text-xs text-slate-600" data-testid="manual-ccl-selected-file">{file.name}</span>}
        <button
          type="button"
          onClick={handleUpload}
          disabled={!file || uploading}
          className="inline-flex items-center gap-1.5 rounded bg-teal px-2.5 py-1.5 text-xs font-medium text-white hover:bg-teal/90 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="manual-ccl-upload-btn"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          {uploading ? "Uploading" : "Upload and check"}
        </button>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" aria-label="Loading manual CCL status" />}
      </div>

      {state?.manual_ccl_uploaded_at && !approved && (
        <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3" data-testid="manual-ccl-results-panel">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-800">
            {passed ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
            {passed ? "Manual CCL check passed" : "Manual CCL check needs review"}
          </div>
          <FlagList flags={flags} />
          {state?.manual_ccl_structural_report && (
            <p className="mt-2 text-[11px] text-slate-500" data-testid="manual-ccl-structural-report">
              Structural validator findings: {state.manual_ccl_structural_report.validator_errors?.length || 0}
            </p>
          )}
          {!passed && (
            <label className="mt-3 flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={override} onChange={(event) => setOverride(event.target.checked)} data-testid="manual-ccl-override-checkbox" />
              Override the manual CCL check
            </label>
          )}
          {!passed && override && (
            <textarea
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
              placeholder="Reason for override"
              className="mt-2 min-h-16 w-full rounded border border-slate-300 bg-white p-2 text-xs text-slate-800 focus:border-teal focus:outline-none"
              data-testid="manual-ccl-override-reason"
            />
          )}
          <button
            type="button"
            onClick={handleApprove}
            disabled={!canApprove || approving}
            className="mt-3 inline-flex items-center gap-1.5 rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="manual-ccl-approve-btn"
          >
            {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            Approve letter (does not send)
          </button>
          {/* BM-H3885: "Approve and use for client pack" read as a send. It
              never was: approval only marks this letter as the one to use. */}
          <p className="mt-2 text-[11px] text-slate-600" data-testid="manual-ccl-approve-note">
            Approving does not send anything. It marks this letter as the one to use, and it only
            goes to the client when someone presses Send to Client for Signing. If the letter was
            already emailed to the client, do not send it again: use "CCL and welcome pack sent off
            CaseMinder" below to record the send.
          </p>
        </div>
      )}

      {approved && (
        <button type="button" onClick={loadState} className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700" data-testid="manual-ccl-refresh-btn">
          <RotateCcw className="h-3 w-3" /> Refresh status
        </button>
      )}
    </div>
  );
}
